import { ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { keyframes, styled, StyledProps } from '../../stitches.config';
import { Icon } from '..';

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const StyledPopover = styled(PopoverPrimitive.Root, {});

const StyledPortal = styled(PopoverPrimitive.Portal, {});

const StyledTrigger = styled(PopoverPrimitive.Trigger, {});

const StyledContent = styled(PopoverPrimitive.Content, {
  'padding': '$7',
  'zIndex': 10,
  'backgroundColor': 'white',
  'borderRadius': '$medium',
  'boxShadow': 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  '@media (prefers-reduced-motion: no-preference)': {
    'animationDuration': '400ms',
    'animationTimingFunction': 'cubic-bezier(0.16, 1, 0.3, 1)',
    'animationFillMode': 'forwards',
    'willChange': 'transform, opacity',
    '&[data-state="open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
});

const StyledArrow = styled(PopoverPrimitive.Arrow, {
  fill: 'white',
});

const StyledClose = styled(PopoverPrimitive.Close, {
  'all': 'unset',
  'fontFamily': 'inherit',
  'borderRadius': '100%',
  'height': 25,
  'width': 25,
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'color': '$slate11',
  'cursor': 'pointer',
  'position': 'absolute',
  'top': 2,
  'right': 2,
  '&:hover': { backgroundColor: '$slate4', color: '$purple9' },
  '&:focus-visible': { boxShadow: `0 0 0 2px $colors$slate7`, color: '$purple9' },
});

export type PopoverProps = StyledProps<PopoverPrimitive.PopoverProps> & {
  isOpen?: boolean;
  trigger: ReactNode;
  triggerProps?: StyledProps<PopoverPrimitive.PopoverTriggerProps>;
  portalProps?: StyledProps<PopoverPrimitive.PopoverPortalProps>;
  contentProps?: StyledProps<PopoverPrimitive.PopoverContentProps>;
  hasArrow?: boolean;
  arrowProps?: StyledProps<PopoverPrimitive.PopoverArrowProps>;
  hasClose?: boolean;
  closeProps?: StyledProps<PopoverPrimitive.PopoverCloseProps>;
  onOpenChange?: (isOpen: boolean) => unknown;
};

export function Popover(props: PopoverProps) {
  const {
    children,
    isOpen,
    trigger,
    triggerProps = {},
    portalProps = {},
    contentProps = {},
    hasArrow = true,
    arrowProps = {},
    hasClose = true,
    closeProps = {},
    onOpenChange,
    ...popoverProps
  } = props;

  const onClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  return (
    <StyledPopover {...popoverProps} open={isOpen} onOpenChange={onOpenChange}>
      <StyledTrigger {...triggerProps} asChild>
        {trigger}
      </StyledTrigger>
      <StyledPortal {...portalProps}>
        <StyledContent sideOffset={5} collisionPadding={20} {...contentProps}>
          {hasArrow && <StyledArrow {...arrowProps} />}
          {hasClose && (
            <StyledClose {...closeProps}>
              <Icon icon="close" onClick={onClose} />
            </StyledClose>
          )}
          {children}
        </StyledContent>
      </StyledPortal>
    </StyledPopover>
  );
}
